/**
 * @fileoverview Syndicator Showdown — Root Application Router
 * @module App
 *
 * Key safety layers:
 *  1. ErrorBoundary  — catches any render-time crash and shows a friendly
 *     fallback instead of a blank white/dark screen.
 *  2. AuthGate       — waits for Firebase's first onAuthStateChanged fire
 *     before rendering any routes, eliminating the race-condition blank screen.
 *  3. ProtectedRoute — per-route spinner + role check (unchanged).
 *  4. SessionGuard   — hard 4-hour session ceiling (from login) *and* the
 *     4-hour inactivity auto-logout (from last activity), applied to every
 *     route — player and admin alike.
 */

import React, { useState, useEffect, useRef, Component } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation,
} from 'react-router-dom';

import { LandingPage }          from './pages/LandingPage';
import { UserDashboard }        from './pages/UserDashboard';
import { SelectionPage }        from './pages/SelectionPage';
import { LeaderboardPage }      from './pages/LeaderboardPage';
import LeaderboardsPage         from './pages/LeaderboardsPage';
import AdminSetupPage           from './pages/admin/AdminSetupPage';
import AdminSchedulePage        from './pages/admin/AdminSchedulePage';
import { AdminSlatesPage }      from './pages/admin/AdminSlatesPage';
import { MyStablePage }         from './pages/MyStablePage';
import { AuthPage }             from './pages/AuthPage';
import { ProtectedRoute }       from './components/layout/ProtectedRoute';
import { AdminResultsPage }     from './pages/admin/AdminResultsPage';
import { AdminOddsPage }        from './pages/admin/AdminOddsPage';
import { AdminScratchingsPage } from './pages/admin/AdminScratchingsPage';
import { AdminLeaderboardPage } from './pages/admin/AdminLeaderboardPage';
import AdminLeaguePage          from './pages/admin/AdminLeaguePage';
import { AdminUsersPage }      from './pages/admin/AdminUsersPage';
import AdminFlagsPage          from './pages/admin/AdminFlagsPage';
import AdminAnnouncementsPage  from './pages/admin/AdminAnnouncementsPage';
import { AppShell }            from './components/shell/AppShell';
import { PlaceholderPage }     from './pages/PlaceholderPage';
import { ShowdownsPage }       from './pages/ShowdownsPage';
import { MarketPage }          from './pages/MarketPage';
import { DashboardPage }      from './pages/DashboardPage';
import { MyStableNew }        from './pages/MyStableNew';
import { AccountPage }       from './pages/AccountPage';
import { GameRulesPage }        from './pages/GameRulesPage';
import { PrivacyPage }          from './pages/PrivacyPage';
import LeaguePage               from './pages/LeaguePage';
import ActionPage               from './pages/ActionPage';
import { ShareInvitePage }      from './pages/ShareInvitePage';
import { ShareStablePage }      from './pages/ShareStablePage';
import { PublicStableProfilePage } from './pages/PublicStableProfilePage';
import { AdminLayout }          from './components/admin/AdminLayout';
import { onAuthStateChanged, signOut }       from 'firebase/auth';
import { auth, db }                         from './services/firebase';
import { doc, updateDoc, serverTimestamp }  from 'firebase/firestore';
import { useInactivityLogout, resetInactivityClock } from './hooks/useInactivityLogout';
import IdleWarningModal                     from './components/shell/IdleWarningModal';
import { writePresence, PRESENCE_HEARTBEAT_MS } from './utils/presence';

// ─── 1. Error Boundary ────────────────────────────────────────────────────────
// Class component required — hooks cannot catch render errors.

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Render crash:', error, info.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      const isLoggedIn = !!auth.currentUser;
      return (
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: '100dvh', background: '#07111F',
          fontFamily: 'sans-serif', color: '#fff', padding: '24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: 700 }}>
            Something went wrong loading this page
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#94a3b8', maxWidth: '400px' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', backgroundColor: '#3B82F6', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600,
              }}
            >
              Try Again
            </button>
            <a
              href={isLoggedIn ? '/dashboard' : '/'}
              style={{
                padding: '10px 24px', backgroundColor: 'transparent', color: '#9CA3AF',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px',
                cursor: 'pointer', fontSize: '14px', textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              {isLoggedIn ? 'Back to Dashboard' : 'Return to Home'}
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── 2. Auth Gate — blocks routing until Firebase resolves initial state ──────
// Prevents the "blank screen on first load / after refresh" race condition.

const SPINNER_STYLE = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '100dvh', background: '#07111F',
};
const SPIN_RING = {
  width: '36px', height: '36px',
  border: '3px solid rgba(255,255,255,0.12)',
  borderTopColor: '#1A4FFF', borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const AuthGate = ({ children }) => {
  // undefined = still waiting for first Firebase response
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // onAuthStateChanged fires once immediately (sync on cached state,
    // async on cold start). We only need to know it has fired at least once.
    const unsub = onAuthStateChanged(auth, () => setReady(true));
    return unsub;
  }, []);

  if (!ready) {
    return (
      <div style={SPINNER_STYLE}>
        <div style={SPIN_RING} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return children;
};

const NotFoundRedirect = () => <Navigate to="/" replace />;

// ─── 3. Session Guard — inactivity timeout + max session age enforcement ─────

// Warn 5 minutes before the inactivity auto-logout fires, so an active-but-
// quiet user (e.g. reading a long page) gets a chance to stay signed in.
const INACTIVITY_WARN_BEFORE_MS = 5 * 60 * 1000;

// Heartbeat interval — write lastActive every 5 minutes while app is open.
// This powers the "Currently Logged In" stat in AdminPlayerStatsBar.
const HEARTBEAT_MS = 5 * 60 * 1000;

// Hard session ceiling: 4 hours from login, regardless of activity or shutdowns.
// Applies equally to player and admin accounts.
const MAX_SESSION_AGE_MS = 4 * 60 * 60 * 1000;

// Key written to localStorage on every successful login (persists through
// full computer shutdown — this is what makes the check work after a restart).
const SESSION_TS_KEY = 'ss_login_time';

async function writeLastActive(uid) {
  try {
    await updateDoc(doc(db, 'users', uid), { lastActive: serverTimestamp() });
  } catch {
    // Silently ignore — not critical if it fails (e.g. offline)
  }
}

const SessionGuard = ({ children }) => {
  const [user, setUser] = useState(null);
  const [idleWarning, setIdleWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // P0 fix (2026-07-04): distinguishes a genuine interactive sign-in (this
  // callback firing again, later, with a user, after already having fired
  // at least once before — including an earlier `null`) from the very
  // first onAuthStateChanged resolution on page load (which may *already*
  // report an existing, possibly hours-old, persisted session on refresh —
  // that case must NOT reset the inactivity clock, since it's exactly the
  // "was this tab left open past the limit" check the guard exists for).
  // Only the former should ever count as fresh activity.
  const hasFiredOnceRef = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      const isFreshInteractiveSignIn = hasFiredOnceRef.current && !!u;
      hasFiredOnceRef.current = true;

      setUser(u);
      if (u) {
        writeLastActive(u.uid); // write on sign-in / page load
        if (isFreshInteractiveSignIn) {
          console.info('[SESSION] Resetting inactivity timers after login', {
            uid: u.uid,
            now: Date.now(),
          });
          resetInactivityClock();
        }
      } else {
        // Any signout path (inactivity, manual, session expiry) clears the
        // login timestamp so a fresh login always starts the clock from zero.
        localStorage.removeItem(SESSION_TS_KEY);
        setIdleWarning(false);
      }
    });
  }, []);

  // 4-hour *inactivity* auto-logout — resets on mouse/keyboard/touch/scroll
  // activity, re-checked immediately on focus/visibility/pageshow so a
  // laptop that slept past the limit signs out the moment it wakes.
  // Mounted once here (not per-shell) so it applies to admin routes too.
  useInactivityLogout(user, {
    warnBeforeMs: INACTIVITY_WARN_BEFORE_MS,
    onWarn: () => setIdleWarning(true),
    onResetWarn: () => setIdleWarning(false),
  });

  // Session age check — runs immediately on app load (including after a full
  // computer shutdown/restart) and every 5 minutes while the tab stays open.
  // This is the hard outer ceiling independent of activity.
  useEffect(() => {
    function checkSessionExpiry() {
      const loginTime = localStorage.getItem(SESSION_TS_KEY);
      if (!loginTime) return; // no record — let Firebase's own auth state decide
      const ageMs = Date.now() - Number(loginTime);
      if (ageMs >= MAX_SESSION_AGE_MS) {
        localStorage.removeItem(SESSION_TS_KEY);
        signOut(auth).catch(() => {});
        navigate('/signin', {
          replace: true,
          state: { reason: 'session_expired' },
        });
      }
    }

    checkSessionExpiry(); // critical: catches expired sessions on first load after shutdown
    const interval = setInterval(checkSessionExpiry, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  // Periodic heartbeat while signed in
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => writeLastActive(user.uid), HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [user]);

  // Phase 5 — presence tracking (users/{uid}.presence), the signal the
  // email-fallback Cloud Function uses to decide RUNNER_UPCOMING/RACE_RESULT
  // email eligibility. Updated on route change immediately below, and on a
  // 60s heartbeat + visibility/focus regain further down — never relies on
  // "unload" alone (browsers don't guarantee it fires).
  useEffect(() => {
    if (!user) return;
    if (document.visibilityState === 'visible') {
      writePresence(user.uid, { isActive: true, currentPath: location.pathname });
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    const pingIfVisible = () => {
      if (document.visibilityState === 'visible') {
        writePresence(user.uid, { isActive: true, currentPath: window.location.pathname });
      }
    };
    document.addEventListener('visibilitychange', pingIfVisible);
    window.addEventListener('focus', pingIfVisible);
    const interval = setInterval(pingIfVisible, PRESENCE_HEARTBEAT_MS);
    return () => {
      document.removeEventListener('visibilitychange', pingIfVisible);
      window.removeEventListener('focus', pingIfVisible);
      clearInterval(interval);
    };
  }, [user]);

  return (
    <>
      {children}
      <IdleWarningModal
        visible={idleWarning}
        onStaySignedIn={() => setIdleWarning(false)}
      />
    </>
  );
};

// ─── Root Router ──────────────────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      {/* AuthGate resolves Firebase's initial auth state before any route renders */}
      <AuthGate>
        {/* ErrorBoundary catches any render crash inside the app */}
        <ErrorBoundary>
          <SessionGuard>
            <Routes>

              {/* Public routes */}
              <Route path="/"                     element={<LandingPage />} />
              <Route path="/rules"                element={<GameRulesPage />} />
              <Route path="/privacy"             element={<PrivacyPage />} />
              <Route path="/league"               element={<LeaguePage />} />
              <Route path="/signin"               element={<AuthPage />} />
              {/* One-time tokenised action links — works without login */}
              <Route path="/action/:tokenId"      element={<ActionPage />} />
              {/* Public share pages — no auth required */}
              <Route path="/share/invite/:referralCode" element={<ShareInvitePage />} />
              <Route path="/share/stable/:shareId"      element={<ShareStablePage />} />
              {/* Public stable profile — works logged out (Phase 7) */}
              <Route path="/stable/:stableSlug"         element={<PublicStableProfilePage />} />

              {/* Logged-in user dashboard */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppShell><DashboardPage /></AppShell>
                </ProtectedRoute>
              } />

              {/* Protected player routes */}
              <Route path="/slate/:slateId" element={
                <ProtectedRoute>
                  <AppShell><SelectionPage /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/my-stable" element={
                <ProtectedRoute>
                  <AppShell><MyStableNew /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/leaderboard/:slateId" element={
                <ProtectedRoute>
                  <AppShell><LeaderboardPage /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/showdowns" element={
                <ProtectedRoute>
                  <AppShell><ShowdownsPage /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/market" element={
                <ProtectedRoute>
                  <AppShell><MarketPage /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/leaderboards" element={
                <ProtectedRoute>
                  <AppShell><LeaderboardsPage /></AppShell>
                </ProtectedRoute>
              } />
              {/* Old placeholder path — keep working for any existing bookmarks/links */}
              <Route path="/leaderboard" element={<Navigate to="/leaderboards" replace />} />
              <Route path="/community" element={
                <ProtectedRoute>
                  <AppShell><PlaceholderPage path="/community" label="Community" icon="👥" /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/learn" element={
                <ProtectedRoute>
                  <AppShell><PlaceholderPage path="/learn" label="Learn" icon="🎓" /></AppShell>
                </ProtectedRoute>
              } />
              <Route path="/account" element={
                <ProtectedRoute>
                  <AppShell><AccountPage /></AppShell>
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSlatesPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/setup" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSetupPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/setup/:slateId" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSetupPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/new-game" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSetupPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/schedule" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSchedulePage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/odds" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminOddsPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/scratchings" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminScratchingsPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/results" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminResultsPage /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/leaderboard" element={
                <ProtectedRoute adminOnly>
                  <AdminLeaderboardPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/leagues" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminLeaguePage /></AdminLayout>
                </ProtectedRoute>
              } />

              <Route path="/admin/users" element={
                <ProtectedRoute adminOnly>
                  <AdminUsersPage />
                </ProtectedRoute>
              } />

              <Route path="/admin/flags" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminFlagsPage /></AdminLayout>
                </ProtectedRoute>
              } />

              <Route path="/admin/announcements" element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminAnnouncementsPage /></AdminLayout>
                </ProtectedRoute>
              } />

              {/* Fallback */}
              <Route path="*" element={<NotFoundRedirect />} />

            </Routes>
          </SessionGuard>
        </ErrorBoundary>
      </AuthGate>
    </BrowserRouter>
  );
}

export default App;
